import { useMemo } from 'react';
import classnames from 'classnames';
import useAppMethods from '../../hooks/utility/useAppMethods';
import RestrictedTooltip from './restricted/RestrictedTooltip';
import { RESTRICTED_IMAGE_STYLE } from './restricted/constants';
import BackIcon from '../../assets/logo/BackIcon';
import NestedModifiers from './nested/NestedModifiers';
import { BasketLineItemModifier } from '../../store/types';
import ModifierToggleButton from './ModifierToggleButton';
import RadioButton from '../buttons/RadioButton';
import { CompiledModifier, CompiledProduct } from '../../providers/MenuProvider';
import useTheme from '../../hooks/ui/useTheme';

interface Props {
  data: CompiledModifier | CompiledProduct;
  quantity: number;
  isSelected: boolean;
  hasMultiply: boolean;
  isSingleSelect: boolean;
  isDeselectable?: boolean;
  toggle: (modifier: CompiledModifier | CompiledProduct) => void;
  // Clears a selected option that has nested modifier groups. The row itself keeps opening the sub-item
  // editor for such options, so the radio becomes the deselect target (as "−" is for multi-select).
  deselect?: (modifier: CompiledModifier | CompiledProduct) => void;
  adjustQuantity: (step: number) => void;
  selectedModifiers: BasketLineItemModifier[];
  isIncreasable: boolean;
  isUpsell?: boolean;
  isRestricted?: boolean;
  restrictedTooltip?: string;
}

export default function Modifier(props: Props) {
  const {
    data,
    isSelected,
    isSingleSelect,
    isDeselectable,
    toggle,
    deselect,
    quantity,
    adjustQuantity,
    selectedModifiers,
    isIncreasable,
    hasMultiply,
    isUpsell,
    isRestricted,
    restrictedTooltip,
  } = props;
  const { formatCurrency } = useAppMethods();
  const theme = useTheme();
  const canBeIncreased = (isIncreasable && !isSelected) || (isSelected && isIncreasable && hasMultiply);
  const hasEditButton = !isSelected && !!data?.modifier_group_ids?.length;

  const hasNestedModifiers = isSelected && !!data?.modifier_group_ids?.length;
  const hasImage = isUpsell && 'thumbnail_url' in data && !!data.thumbnail_url;
  const isInteractive = data.is_available && !isRestricted;
  const canDeselectNested = isSelected && hasNestedModifiers && isDeselectable && !!deselect;

  function toggleModifier(): void {
    // Deselecting is always safe, so it is not gated by availability or restriction: a selected option
    // that became unavailable or restricted while the modal was open must still be clearable.
    if (isSelected && !hasNestedModifiers && isDeselectable) {
      toggle(data);

      return;
    }

    if (isSelected && !hasNestedModifiers) {
      return;
    }

    if (!data.is_available) {
      return;
    }

    if (isRestricted) {
      return;
    }

    if (!isSingleSelect && !canBeIncreased) {
      return;
    }

    toggle(data);
  }

  function getPrice() {
    const isDiscount: boolean = data.base_price > data.actual_price;

    return data.is_available ? (
      <div className="flex flex-row items-center my-auto flex-shrink-0">
        {isDiscount && (
          <p className={priceStyle(isDiscount, data.is_available)}>
            + {formatCurrency(data.base_price * (quantity || 1))}
          </p>
        )}
        <p className={highlightedPriceStyle}>+ {formatCurrency(data.actual_price * (quantity || 1))}</p>
      </div>
    ) : null;
  }

  const modifierGroups = useMemo(
    () =>
      data.modifier_groups?.filter((modifierGroup) =>
        selectedModifiers?.some((modifier) => modifier.modifier_group_id === modifierGroup.id),
      ),
    [selectedModifiers, data.modifier_groups],
  );

  const button = (
    <button
      // `data-selected` carries the state that is otherwise conveyed only by a checkbox/radio graphic,
      // and `data-actual-price` exposes the surcharge as a NUMBER so a spec can compute the expected
      // subtotal instead of parsing "+ 3,00 €" and inheriting the active locale's decimal separator
      // and currency placement.
      data-test={`modifier-${data.id}`}
      data-selected={isSelected ? 'true' : 'false'}
      data-actual-price={data.actual_price}
      className={classnames('w-full', {
        'touchable-highlight': isInteractive,
        'cursor-not-allowed': isRestricted,
      })}
      onClick={toggleModifier}
    >
      <div className={classnames(container)}>
        <div className={classnames('flex-1 my-auto')}>
          <p
            className={classnames('text-left text-sm', {
              'opacity-25': !data.is_available,
              'opacity-50': data.is_available && isRestricted,
              'flex items-center gap-6': hasImage,
            })}
          >
            {hasImage && (
              <img
                className="size-12 rounded-lg"
                src={data.thumbnail_url}
                alt="Logo"
                style={isRestricted ? RESTRICTED_IMAGE_STYLE : undefined}
              />
            )}
            {data.name}
          </p>
        </div>
        <div className={classnames('my-auto flex items-center gap-4', { 'opacity-50': isRestricted })}>
          <span>{getPrice()}</span>
          {isSingleSelect ? (
            canDeselectNested ? (
              <button
                type="button"
                // Negative margin keeps the radio where it is while widening the tap target to the "−" button's size.
                className="p-[2px] -m-[2px] rounded-full"
                data-test={`modifier-${data.id}-deselect`}
                aria-label={data.name}
                onClick={(e) => {
                  e.stopPropagation();
                  deselect(data);
                }}
              >
                <RadioButton
                  isSelected={isSelected}
                  className="border-brand-text-secondary w-[20px] h-[20px]"
                  innerClassName="w-[12px] h-[12px]"
                />
              </button>
            ) : (
              <RadioButton
                isSelected={isSelected}
                className="border-brand-text-secondary w-[20px] h-[20px]"
                innerClassName="w-[12px] h-[12px]"
              />
            )
          ) : (
            <ModifierToggleButton
              isIncreasable={canBeIncreased}
              isSelected={isSelected}
              quantity={quantity}
              isUpsell={isUpsell}
              increase={() => {
                if (isSelected) {
                  adjustQuantity(1);

                  return;
                }

                toggleModifier();
              }}
              decrease={() => adjustQuantity(-1)}
            />
          )}
          {hasEditButton && <BackIcon className="size-3 rotate-180" color={theme.color.brand.text.secondary} />}
        </div>
      </div>
      {hasNestedModifiers && <NestedModifiers data={modifierGroups} selectedModifiers={selectedModifiers} />}
    </button>
  );

  return <RestrictedTooltip title={isRestricted ? restrictedTooltip : undefined}>{button}</RestrictedTooltip>;
}

const container =
  "flex flex-row w-full my-[8px] before:relative after:content-[''] after:absolute after:-top-0 after:-bottom-0";

const priceStyle = (isDiscount?: boolean, isAvailable?: boolean) =>
  classnames('text-muted-foreground line-clamp-1 text-right text-sm', {
    'line-through': isDiscount,
    'opacity-25': !isAvailable,
  });

const highlightedPriceStyle = 'text-brand-text-primary text-right text-sm font-medium line-clamp-1 ml-xs';
